import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ChevronDown, Menu, X } from 'lucide-react';

import { useOutsideClick } from '../hooks/useOutsideClick';
import { useScroll } from '../hooks/useScroll';

import logo from './../assets/images/schoolhack-logo.png';

const REGISTER_URL = 'https://app.schoolhack.ai/auth/register';
const LOGIN_URL = 'https://app.schoolhack.ai/auth/login';

const FEATURES = [
  { name: 'Interactive Notes', desc: 'Notes that quiz you back', to: '/features/interactive-notes' },
  { name: 'Interactive Flashcards', desc: 'Auto-built from your notes', to: '/features/interactive-flashcards' },
  { name: 'Interactive Quizzes', desc: 'Instant feedback, instant learning', to: '/features/interactive-quizzes' },
  { name: 'Study Podcasts', desc: 'Turn notes into audio', url: 'https://app.schoolhack.ai' },
  { name: 'Interactive Teacher', desc: 'Your always-on AI tutor', to: '/features/interactive-teacher' },
  { name: 'Collaborative Mode', desc: 'Study live with friends', to: '/features/collaborative-mode' },
];

const Header = ({ isScrolling }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);

  const featuresRef = useRef(null);
  const { pathname } = useLocation();

  useScroll(!isMobileMenuOpen);
  useOutsideClick(isFeaturesOpen ? featuresRef : null, () => setIsFeaturesOpen(false));

  // close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsFeaturesOpen(false);
  }, [pathname]);

  const navLink = (active) =>
    twMerge(
      'text-sm font-medium transition-colors',
      active ? 'text-accent' : 'text-white/70 hover:text-white',
    );

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 md:px-6 md:pt-4">
      <nav
        className={twMerge(
          'relative mx-auto flex h-[64px] w-full max-w-[80rem] items-center justify-between rounded-2xl border pl-5 pr-3 transition-all duration-500 md:pl-6 md:pr-3',
          isScrolling
            ? 'border-white/10 bg-[#0b100e]/85 shadow-[0_14px_40px_-16px_rgba(0,0,0,0.8)] backdrop-blur-xl'
            : 'border-white/[0.08] bg-[#0b100e]/60 backdrop-blur-xl',
        )}
      >
        {/* Logo */}
        <Link to="/" title="School Hack" className="flex items-center">
          <img src={logo} alt="School Hack" className="h-8 w-auto md:h-9" />
        </Link>

        {/* Desktop nav */}
        <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 lg:flex">
          <Link to="/" className={navLink(pathname === '/')}>
            Home
          </Link>

          <div className="relative" ref={featuresRef}>
            <button
              type="button"
              onClick={() => setIsFeaturesOpen((v) => !v)}
              className={twMerge(
                'flex items-center gap-1 text-sm font-medium transition-colors',
                isFeaturesOpen ? 'text-white' : 'text-white/70 hover:text-white',
              )}
            >
              Our Features
              <ChevronDown
                size={15}
                className={twMerge('transition-transform duration-300', isFeaturesOpen && 'rotate-180')}
              />
            </button>

            <div
              className={twMerge(
                'absolute left-1/2 top-[calc(100%+16px)] w-[320px] -translate-x-1/2 origin-top rounded-2xl p-2 transition-all duration-300',
                'glass',
                isFeaturesOpen
                  ? 'pointer-events-auto scale-100 opacity-100'
                  : 'pointer-events-none scale-95 opacity-0',
              )}
            >
              {FEATURES.map((f) =>
                f.to ? (
                  <Link
                    key={f.name}
                    to={f.to}
                    className="group flex flex-col rounded-xl px-3.5 py-2.5 transition-colors hover:bg-accent/10"
                  >
                    <span className="text-sm font-medium text-white group-hover:text-accent">
                      {f.name}
                    </span>
                    <span className="text-xs text-white/45">{f.desc}</span>
                  </Link>
                ) : (
                  <a
                    key={f.name}
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex flex-col rounded-xl px-3.5 py-2.5 transition-colors hover:bg-accent/10"
                  >
                    <span className="text-sm font-medium text-white group-hover:text-accent">
                      {f.name}
                    </span>
                    <span className="text-xs text-white/45">{f.desc}</span>
                  </a>
                ),
              )}
            </div>
          </div>

          {/* <Link to="/career" className={navLink(pathname.startsWith('/career'))}>
            Careers
          </Link> */}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden items-center gap-2 lg:flex">
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noreferrer"
            className="btn-primary px-5 py-2.5 text-sm"
          >
            Sign up for free
          </a>
          <a
            href={LOGIN_URL}
            // target="_blank"
            rel="noreferrer"
            className="rounded-full border border-white/12 bg-white/[0.06] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-white/20 hover:bg-white/10"
          >
            Login
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          title="menu"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white lg:hidden"
          onClick={() => setIsMobileMenuOpen((v) => !v)}
        >
          {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile dropdown */}
      <div
        className={twMerge(
          'glass absolute left-1/2 top-[76px] w-[calc(100%-24px)] -translate-x-1/2 overflow-hidden rounded-3xl transition-all duration-300 md:w-[calc(100%-48px)] lg:hidden',
          isMobileMenuOpen ? 'max-h-[460px] py-4 opacity-100' : 'max-h-0 py-0 opacity-0',
        )}
      >
        <div className="flex flex-col gap-1 px-3">
          <Link to="/" className="rounded-xl px-4 py-3 text-sm font-medium text-white/90 hover:bg-white/5">
            Home
          </Link>
          {FEATURES.map((f) =>
            f.to ? (
              <Link
                key={f.name}
                to={f.to}
                className="rounded-xl px-4 py-3 text-sm font-medium text-white/70 hover:bg-white/5"
              >
                {f.name}
              </Link>
            ) : (
              <a
                key={f.name}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl px-4 py-3 text-sm font-medium text-white/70 hover:bg-white/5"
              >
                {f.name}
              </a>
            ),
          )}
          <Link to="/career" className="rounded-xl px-4 py-3 text-sm font-medium text-white/90 hover:bg-white/5">
            Careers
          </Link>
          <div className="mt-2 flex flex-col gap-2 px-1">
            <a href={LOGIN_URL} target="_blank" rel="noreferrer" className="btn-ghost px-4 py-3 text-sm">
              Login
            </a>
            <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="btn-primary px-4 py-3 text-sm">
              Sign up for free
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
